import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { financialsApi } from '@/lib/financialsApi';
import { parseTypedAmount, toNumber, type TypedAmount } from '@/lib/number';
import { showSuccess, showError } from '@/lib/apiErrors';
import { useMembership } from '@/hooks/useMembership';
import { usePonds } from '@/hooks/usePonds';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { X, Receipt } from 'lucide-react';
import { cn } from '@/lib/cn';
import { ALLOCATION_TYPE_OPTIONS } from '@/types/financials';
import type { ExpenseCreate, ExpenseUpdate, Expense } from '@/types/financials';

interface ExpenseFormProps {
  open: boolean;
  onClose: () => void;
  farmId: string;
  /** Pass an expense object to edit; null/undefined = create new */
  expense?: Expense | null;
  onSuccess?: () => void;
}

export function ExpenseForm({
  open,
  onClose,
  farmId,
  expense,
  onSuccess,
}: ExpenseFormProps) {
  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/25 backdrop-blur-sm z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Drawer panel */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 w-full sm:w-[480px]',
          'bg-white shadow-xl border-l border-gray-200',
          'transform transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Unmount form when closed so state resets on reopen */}
        {open && (
          <ExpenseFormInner
            farmId={farmId}
            expense={expense}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        )}
      </div>
    </>
  );
}

// ============================================================================
// Inner form component (remounts on open)
// ============================================================================

interface Cycle {
  id: string;
  pond_name: string;
  cycle_number: number;
  status: string;
}

interface ExpenseFormInnerProps {
  farmId: string;
  expense?: Expense | null;
  onClose: () => void;
  onSuccess?: () => void;
}

function ExpenseFormInner({
  farmId,
  expense: initialExpense,
  onClose,
  onSuccess,
}: ExpenseFormInnerProps) {
  const { membership } = useMembership();
  const isEditing = !!initialExpense;
  const expenseId = initialExpense?.id;

  // Form state — initialized from props (component remounts on drawer open)
  const [category, setCategory] = useState(initialExpense?.category || '');
  const [amount, setAmount] = useState(
    initialExpense ? String(toNumber(initialExpense.amount)) : ''
  );
  const [expenseDate, setExpenseDate] = useState(
    initialExpense?.expense_date || new Date().toISOString().split('T')[0]
  );
  const [description, setDescription] = useState(initialExpense?.description || '');
  const [allocationType, setAllocationType] = useState(initialExpense?.allocation_type || 'cycle');
  const [cycleId, setCycleId] = useState(initialExpense?.cycle_id || '');
  const [pondId, setPondId] = useState(initialExpense?.pond_id || '');
  const [vendor, setVendor] = useState(initialExpense?.vendor || '');
  const [referenceNumber, setReferenceNumber] = useState(initialExpense?.reference_number || '');
  const [quantity, setQuantity] = useState(
    initialExpense?.quantity != null ? String(toNumber(initialExpense.quantity)) : ''
  );
  const [unit, setUnit] = useState(initialExpense?.unit || '');
  const [unitCost, setUnitCost] = useState(
    initialExpense?.unit_cost != null ? String(toNumber(initialExpense.unit_cost)) : ''
  );
  const [notes, setNotes] = useState(initialExpense?.notes || '');

  // Fetch categories
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['financials', 'categories'],
    queryFn: () => financialsApi.listCategories(),
    staleTime: 5 * 60 * 1000,
  });
  const categories = categoriesData?.data ?? [];

  // Fetch cycles for dropdown
  const { data: cyclesData } = useQuery({
    queryKey: ['cycles', membership?.farmId],
    queryFn: async () => {
      const response = await api.get<{ data: Cycle[] }>(
        `/api/v1/farms/${membership?.farmId}/cycles`
      );
      return Array.isArray(response) ? response : response?.data || [];
    },
    enabled: !!membership?.farmId,
  });
  const cycles = cyclesData || [];

  // Fetch ponds for dropdown
  const { data: pondsData } = usePonds(membership?.farmId);
  const ponds = pondsData || [];

  // Typed text: "12,5" is 12.5 and "12,500" is 12500; anything else is refused with a message.
  const amountParsed = parseTypedAmount(amount, { thousands: true });
  const quantityParsed = parseTypedAmount(quantity, { thousands: true });
  const unitCostParsed = parseTypedAmount(unitCost, { thousands: true });
  const valueOf = (p: TypedAmount) => ('value' in p ? p.value : null);
  const errorOf = (p: TypedAmount) => ('error' in p ? p.error : undefined);
  const amountValue = valueOf(amountParsed);
  const hasNumberError = [amountParsed, quantityParsed, unitCostParsed].some((p) => 'error' in p);

  // Auto-calculate amount from quantity * unit_cost
  function autoAmount(qtyText: string, costText: string) {
    const qty = valueOf(parseTypedAmount(qtyText, { thousands: true }));
    const cost = valueOf(parseTypedAmount(costText, { thousands: true }));
    if (qty !== null && cost !== null && qty * cost > 0) setAmount((qty * cost).toFixed(2));
  }

  function updateQuantity(newQty: string) {
    setQuantity(newQty);
    autoAmount(newQty, unitCost);
  }

  function updateUnitCost(newCost: string) {
    setUnitCost(newCost);
    autoAmount(quantity, newCost);
  }

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: ExpenseCreate) => financialsApi.createExpense(farmId, data),
    onSuccess: () => {
      showSuccess('Expense created');
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to create expense');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: ExpenseUpdate) =>
      financialsApi.updateExpense(farmId, expenseId!, data),
    onSuccess: () => {
      showSuccess('Expense updated');
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to update expense');
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!category || amountValue === null || hasNumberError || !expenseDate) return;
    const quantityValue = valueOf(quantityParsed);
    const unitCostValue = valueOf(unitCostParsed);

    const baseData = {
      category,
      amount: amountValue,
      expense_date: expenseDate,
      allocation_type: allocationType as 'cycle' | 'pond' | 'farm',
      ...(description.trim() && { description: description.trim() }),
      ...(allocationType === 'cycle' && cycleId && { cycle_id: cycleId }),
      ...(allocationType === 'pond' && pondId && { pond_id: pondId }),
      ...(vendor.trim() && { vendor: vendor.trim() }),
      ...(referenceNumber.trim() && { reference_number: referenceNumber.trim() }),
      ...(quantityValue !== null && { quantity: quantityValue }),
      ...(unit.trim() && { unit: unit.trim() }),
      ...(unitCostValue !== null && { unit_cost: unitCostValue }),
      ...(notes.trim() && { notes: notes.trim() }),
    };

    if (isEditing) {
      updateMutation.mutate(baseData);
    } else {
      createMutation.mutate(baseData as ExpenseCreate);
    }
  }

  if (categoriesLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <LoadingSpinner size={24} className="text-primary-600" />
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-blue-50 p-2">
            <Receipt className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit Expense' : 'Add Expense'}
            </h2>
            <p className="text-sm text-gray-500">
              {isEditing ? 'Update expense details' : 'Record a new expense'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Category */}
        {categoriesLoading ? (
          <div className="flex items-center gap-2 py-2">
            <LoadingSpinner size={16} />
            <span className="text-sm text-gray-500">Loading categories...</span>
          </div>
        ) : (
          <Select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Select a category..."
            options={categories
              .filter((c) => !c.auto_from_inventory)
              .map((c) => ({ value: c.code, label: c.name }))}
            required
          />
        )}

        {/* Amount */}
        <Input
          label="Amount (PHP)"
          type="text"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={errorOf(amountParsed)}
          placeholder="0.00"
          required
        />

        {/* Expense date */}
        <Input
          label="Expense Date"
          type="date"
          value={expenseDate}
          onChange={(e) => setExpenseDate(e.target.value)}
          required
        />

        {/* Description */}
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of this expense"
        />

        {/* Allocation type */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-gray-700">
            Allocation
          </label>
          <div className="flex gap-2">
            {ALLOCATION_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setAllocationType(opt.value)}
                className={cn(
                  'px-4 py-2 rounded-lg border text-sm font-medium transition-colors',
                  allocationType === opt.value
                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Cycle selector (when allocation is cycle) */}
        {allocationType === 'cycle' && (
          <Select
            label="Cycle"
            value={cycleId}
            onChange={(e) => setCycleId(e.target.value)}
            placeholder="Select a cycle..."
            options={cycles.map((c) => ({
              value: c.id,
              label: `${c.pond_name} #${c.cycle_number} (${c.status})`,
            }))}
          />
        )}

        {/* Pond selector (when allocation is pond) */}
        {allocationType === 'pond' && (
          <Select
            label="Pond"
            value={pondId}
            onChange={(e) => setPondId(e.target.value)}
            placeholder="Select a pond..."
            options={ponds.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          />
        )}

        {/* Vendor */}
        <Input
          label="Vendor"
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          placeholder="e.g., CP Foods, Local supplier"
        />

        {/* Quantity + Unit + Unit Cost row */}
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Quantity"
            type="text"
            inputMode="decimal"
            value={quantity}
            onChange={(e) => updateQuantity(e.target.value)}
            error={errorOf(quantityParsed)}
            placeholder="0"
          />
          <Input
            label="Unit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="kg, L, pc"
          />
          <Input
            label="Unit Cost"
            type="text"
            inputMode="decimal"
            value={unitCost}
            onChange={(e) => updateUnitCost(e.target.value)}
            error={errorOf(unitCostParsed)}
            placeholder="0.00"
          />
        </div>

        {/* Reference number */}
        <Input
          label="Reference Number"
          value={referenceNumber}
          onChange={(e) => setReferenceNumber(e.target.value)}
          placeholder="Invoice or receipt number"
        />

        {/* Notes */}
        <Textarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional notes..."
          rows={3}
        />
      </form>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center gap-3">
        <Button
          variant="primary"
          fullWidth
          loading={isPending}
          disabled={!category || amountValue === null || amountValue <= 0 || hasNumberError || !expenseDate}
          onClick={handleSubmit as unknown as () => void}
        >
          {isEditing ? 'Update Expense' : 'Add Expense'}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
